/**
 * Admin auth (cookie session) + user management.
 *
 * Extracted from `src/services/api.ts` (Fase 10 Slice 10.5).
 *
 * Session (httpOnly cookie):
 *   - POST /admin/login          — issue session cookie
 *   - POST /admin/logout         — clear session cookie
 *   - GET  /admin/check-session  — whoami
 *
 * User management:
 *   - GET  /admin/users          — list
 *   - POST /admin/users          — create
 *   - PUT  /admin/users/{id}     — update role/active/password
 *   - PUT  /admin/users/me/password         — self-change password
 *   - POST /admin/users/{id}/reset-password — admin-reset (temp password)
 *   - PUT  /admin/users/{id}/deactivate     — soft deactivate
 *
 * Re-exported from services/api.ts so existing call-sites keep working.
 */

import { apiClient } from '../client';

// ─── Shared user shape ──────────────────────────────────────────────────────

export interface AdminUserInfo {
  id: number;
  email: string;
  role: string;
  allowed_tabs: string[] | string;
  is_active?: boolean;
  created_at?: string | null;
  updated_at?: string | null;
}

// ─── Session ────────────────────────────────────────────────────────────────

export const adminLogin = async (email: string, password: string): Promise<{ status: string; user: AdminUserInfo }> => {
  const response = await apiClient.post<{ status: string; user: AdminUserInfo }>('/admin/login', { email, password });
  return response.data;
};

export const adminLogout = async (): Promise<{ status: string }> => {
  const response = await apiClient.post<{ status: string }>('/admin/logout');
  return response.data;
};

export const adminCheckSession = async (): Promise<{
  status: string;
  user: AdminUserInfo;
}> => {
  const response = await apiClient.get<{ status: string; user: AdminUserInfo }>('/admin/check-session');
  return response.data;
};

// ─── User management ────────────────────────────────────────────────────────

export const adminListUsers = async (): Promise<AdminUserInfo[]> => {
  const response = await apiClient.get<AdminUserInfo[]>('/admin/users');
  return response.data;
};

export const adminCreateUser = async (email: string, password: string, role: string): Promise<AdminUserInfo> => {
  const response = await apiClient.post<AdminUserInfo>('/admin/users', {
    email,
    password,
    role,
  });
  return response.data;
};

export const adminUpdateUser = async (
  userId: number,
  data: { role?: string; is_active?: boolean; password?: string },
): Promise<AdminUserInfo> => {
  const response = await apiClient.put<AdminUserInfo>(`/admin/users/${userId}`, data);
  return response.data;
};

export const adminChangeOwnPassword = async (
  currentPassword: string,
  newPassword: string,
): Promise<{ status: string }> => {
  const response = await apiClient.put<{ status: string }>('/admin/users/me/password', {
    current_password: currentPassword,
    new_password: newPassword,
  });
  return response.data;
};

export const adminResetUserPassword = async (userId: number): Promise<{ temporary_password: string }> => {
  const response = await apiClient.post<{ temporary_password: string }>(`/admin/users/${userId}/reset-password`);
  return response.data;
};

export const adminDeactivateUser = async (userId: number): Promise<AdminUserInfo> => {
  const response = await apiClient.put<AdminUserInfo>(`/admin/users/${userId}/deactivate`);
  return response.data;
};
